import { useState } from "react";
import type {
  BatchResponse,
  CharacteristicValueInput,
  SetBatchCharacteristicsRequest,
} from "@/types/batches.types";

type Change = SetBatchCharacteristicsRequest["values"][number];

function ownValueOf(
  c: BatchResponse["characteristics"][number]
): CharacteristicValueInput | undefined {
  if (c.state !== "OWN") return undefined;
  const v = c.values[0];
  if (!v) return undefined;
  return v.valueId
    ? { characteristicId: c.characteristicId, valueId: v.valueId }
    : { characteristicId: c.characteristicId, text: v.label };
}

/**
 * Spec 084 — o rascunho da correção. Só as características EDITÁVEIS entram, e só o que mudou vai
 * para a API: reenviar um valor idêntico gravaria autoria e procedência de tela sobre um valor que
 * veio da ordem ou da importação.
 */
export function useBatchEditDraft(batch: BatchResponse) {
  const editable = batch.characteristics.filter((c) => c.editable);
  const initial = new Map(editable.map((c) => [c.characteristicId, ownValueOf(c)]));
  const [values, setValues] = useState(initial);
  const [code, setCode] = useState(batch.batchCode);

  function set(characteristicId: string, value: CharacteristicValueInput | null) {
    setValues((prev) => new Map(prev).set(characteristicId, value ?? undefined));
  }

  const changes: Change[] = [];
  for (const c of editable) {
    const before = initial.get(c.characteristicId);
    const after = values.get(c.characteristicId);
    if (JSON.stringify(before) === JSON.stringify(after)) continue;
    changes.push(after ?? { characteristicId: c.characteristicId, clear: true });
  }
  const missing = editable
    // Obrigatória que faltava ou que tinha valor próprio: voltar a "não informado" é recusado.
    .filter((c) => c.required && (c.missingRequired || c.state === "OWN"))
    .filter((c) => !values.get(c.characteristicId))
    .map((c) => c.name);

  return {
    editable,
    values,
    set,
    changes,
    code,
    setCode,
    codeChanged: code.trim() !== batch.batchCode,
    missing,
  };
}
